import type { SupabaseClient } from '@supabase/supabase-js';

let syncSequence = 0;

// Webhooks can finish after checkout redirects back, or while another tab is open.
// Always re-read the authenticated account; neither a URL nor an event grants access.
export function startSubscriptionSync(
  client: SupabaseClient,
  userId: string,
  refresh: () => Promise<void>,
  browser: Window = window,
): () => void {
  let stopped = false;
  let pending = false;
  let rerun = false;
  const checkoutDeadline = new URLSearchParams(browser.location.search).has('session_id')
    ? Date.now() + 120_000 : 0;
  let nextPoll = Date.now() + 15_000;

  const update = async (queueIfPending = true) => {
    if (stopped || browser.document.visibilityState === 'hidden') return;
    if (pending) {
      if (queueIfPending) rerun = true;
      return;
    }
    pending = true;
    try {
      do {
        rerun = false;
        await refresh();
      } while (rerun && !stopped);
    } catch (error) {
      console.error('[Subscription] Synchronization failed:', error);
    } finally {
      pending = false;
    }
  };
  const onResume = () => { void update(); };
  // Cleanup is asynchronous. A remount must not reuse a channel still leaving.
  const channel = client.channel(`account-subscription:${userId}:${++syncSequence}`)
    .on('postgres_changes', {
      event: '*', schema: 'public', table: 'subscriptions', filter: `user_id=eq.${userId}`,
    }, onResume)
    .on('system', {}, event => {
      if (event.status === 'ok' && event.extension === 'postgres_changes') onResume();
    })
    .subscribe(status => {
      // Recheck after connecting/reconnecting to close the fetch/subscribe gap.
      if (status === 'SUBSCRIBED') onResume();
    });

  browser.addEventListener('focus', onResume);
  browser.addEventListener('online', onResume);
  browser.document.addEventListener('visibilitychange', onResume);
  // Polling also covers a dropped socket or a deployment awaiting the migration.
  const timer = browser.setInterval(() => {
    const now = Date.now();
    if (now < checkoutDeadline || now >= nextPoll) {
      nextPoll = now + 15_000;
      void update(false);
    }
  }, 2_000);

  return () => {
    stopped = true;
    browser.clearInterval(timer);
    browser.removeEventListener('focus', onResume);
    browser.removeEventListener('online', onResume);
    browser.document.removeEventListener('visibilitychange', onResume);
    void client.removeChannel(channel);
  };
}
